import React, { useCallback, useEffect, useRef, useState } from 'react';
import { capitalize, debounce, get, isEqual, omit, pick } from 'lodash';
import { Button, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { clearOfficeList, createLocation, fetchOfficeList } from '../../offices/offices-actions';
import { formItemMap } from '@med-fe/ui';
import {
  getBillingAccount,
  getBillingInfoForLocation,
  getIsLoading,
  getOfficeList,
  getAddressForLocation,
  getOfficeListLoading,
  getOfficeListHasMore,
} from '../../offices/offices-selectors';
import moment from 'moment';
import { billingInfoConfig } from '../configs/billing-info-config';
import { billingAccountInfoConfig } from '../configs/billing-account-info';
import { getAdminEmails, getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { addressConfig } from '../configs/address-config';
import { LocationCategory } from 'apps/mod/src/models/locationCategory-enum';
import { fetchAdminEmails } from 'apps/mod/src/store/user/user-actions';
import { useMsal } from '@azure/msal-react';

function FinanceDetails({ locId, title }) {
  const selectorMap = {
    billing_info: {
      data: getBillingInfoForLocation,
      config: billingInfoConfig,
    },
    office_address: {
      data: getAddressForLocation,
      config: addressConfig,
    },
    billing_account: {
      data: getBillingAccount,
      config: billingAccountInfoConfig,
    },
  };

  const { accounts } = useMsal();
  const data = useSelector((state) => selectorMap[title]['data'](state, locId));
  const isEditAllowed = useSelector(getWriteTabsPermissions).finance;
  const isLoading = useSelector(getIsLoading);
  const officeListLoading = useSelector(getOfficeListLoading);
  const officeListHasMore = useSelector(getOfficeListHasMore);
  const [isDisabled, setIsDisabled]: any = useState(true);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const officeList = useSelector(getOfficeList);
  const [officeSearchPayload, setOfficeSearchPayload] = useState<any>({ page: 0, officeNumber: '' });
  const [locationCategory, setLocationCategory] = useState(data?.locationCategory ? data?.locationCategory : null);
  const adminEmails = useSelector(getAdminEmails);
  const [allowDateClear, setAllowDateClear] = useState<boolean>(false);
  const hasMounted = useRef(false);
  const [closedDate, setClosedDate] = useState(data?.closedDate ? data?.closedDate : null);
  // Removed duplicate declaration of accountingIntegrationDate
  const [accountingIntegrationDate, setAccountingIntegrationDate] = useState(
    data?.accountingIntegrationDate ? data?.accountingIntegrationDate : null
  );
  const [sameOfficeChangeDate, setsameOfficeChangeDate] = useState(
    data?.sameOfficeChangeDate ? data?.sameOfficeChangeDate : null
  );
  const [isClosedDateConfirmVisible, setClosedDateConfirmVisible] = useState(false);
  const [isAccountingIntegrationDateConfirmVisible, setAccountingIntegrationDateConfirmVisible] = useState(false);
  const [isSameOfficeChangeDateConfirmVisible, setsameOfficeChangeDateConfirmVisible] = useState(false);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchAdminEmails());
  }, []);

  useEffect(() => {
    setAllowDateClear(adminEmails?.some((user) => user?.trim().toLowerCase() === accounts[0]?.username));
  }, [adminEmails, accounts]);

  useEffect(() => {
    dispatch(clearOfficeList());
    dispatch(fetchOfficeList({ page: 0 }));
  }, [locId]);

  const onSubmit = (dataFromForm: any) => {
    const getId = (value) => {
      return typeof value === 'object' ? { id: value.id } : { id: value };
    };
    const { ...formData } = {
      ...dataFromForm,
      ...(Object.entries(dataFromForm)
        .filter(([key]) => key === 'mergedInto')
        .some(([_, value]) => value === undefined) && { mergedInto: null }),
      ...(!!dataFromForm.mergedInto && { mergedInto: getId(dataFromForm.mergedInto) }),
      ...{ closedDate: closedDate },
      ...{ accountingIntegrationDate: accountingIntegrationDate },
      ...{ sameOfficeChangeDate: sameOfficeChangeDate },
    };
    let payload: any = {
      id: locId,
    };

    switch (title) {
      case 'billing_info':
        payload = {
          ...payload,
          ...formData,
          ...(!!formData.closedDate && { closedDate: closedDate }),
        };
        break;
      case 'billing_account':
        payload = {
          ...payload,
          ...pick(formData, ['integration', 'sameOfficeSales', 'accountingIntegrationDate']),
          ...(typeof formData.projectName === 'string' && { projectName: formData.projectName.trim() }),
          sameOfficeChangeDate: formData?.sameOfficeChangeDate
            ? moment(formData?.sameOfficeChangeDate).format('YYYY-MM-DD')
            : null,
          billing: {
            id: data.id,
            ...omit(formData, [
              'integration',
              'sameOfficeSales',
              'sameOfficeChangeDate',
              'accountingIntegrationDate',
              'projectName',
              'legacyName',
              'brandingType',
            ]),
          },
          ...(!!formData.accountingIntegrationDate && { accountingIntegrationDate: accountingIntegrationDate }),
        };
        break;
      case 'office_address':
        payload = {
          ...payload,
          ...formData,
        };
        break;
    }
    dispatch(createLocation(payload));
    setIsDisabled(true);
  };

  const onClosedDateSelect = (value) => {
    form.setFieldsValue({ closedDate: value ? value.format('YYYY-MM-DD') : null });
    setClosedDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onAccountingIntegrationDateSelect = (value) => {
    form.setFieldsValue({ accountingIntegrationDate: value ? value.format('YYYY-MM-DD') : null });
    setAccountingIntegrationDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onSameOfficeChangeSelect = (value) => {
    form.setFieldsValue({ sameOfficeChangeDate: value ? value.format('YYYY-MM-DD') : null });
    setsameOfficeChangeDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onConfirmSetVisible = (value, name) => {
    if (name == 'Closed Date') setClosedDateConfirmVisible(value);
    if (name == 'Accounting Integration Date') setAccountingIntegrationDateConfirmVisible(value);
    if (name == 'Same Office Change Date') setsameOfficeChangeDateConfirmVisible(value);
  };

  const debouncedFetchOfficeList = useCallback(
    debounce((searchParams) => {
      dispatch(fetchOfficeList(searchParams));
    }, 150),
    [dispatch]
  );

  const onOfficeSearch = (officeNumber) => {
    if (officeNumber && officeNumber.length >= 3) {
      setOfficeSearchPayload({ page: 0, officeNumber });
      dispatch(clearOfficeList());
      debouncedFetchOfficeList({ officeNumber });
    }
    if (officeNumber === '') {
      setOfficeSearchPayload({ page: 0, officeNumber: '' });
      dispatch(clearOfficeList());
      debouncedFetchOfficeList({});
    }
  };

  const onOfficesScroll = (event) => {
    const target = event.target || event.currentTarget;
    if (
      Math.round(target.scrollTop) + Math.round(target.offsetHeight) >= Math.round(target.scrollHeight - 10) &&
      !officeListLoading &&
      officeListHasMore
    ) {
      const nextPage = officeSearchPayload.page + 1;
      setOfficeSearchPayload({ ...officeSearchPayload, page: nextPage });
      dispatch(
        fetchOfficeList({
          page: nextPage,
          ...(officeSearchPayload.officeNumber && { officeNumber: officeSearchPayload.officeNumber }),
        })
      );
    }
  };

  const onOfficeFocus = () => {
    setOfficeSearchPayload({ page: 0, officeNumber: '' });
    if (officeList.length === 0) {
      dispatch(fetchOfficeList({ page: 0 }));
    }
  };

  const onOfficeClear = () => {
    setOfficeSearchPayload({ page: 0, officeNumber: '' });
    dispatch(clearOfficeList());
    dispatch(fetchOfficeList({ page: 0 }));
  };

  const onLocationCategorySelect = (value) => {
    switch (value) {
      case 'ACQUISITION':
        form.resetFields(['newOfficeYear', 'jointVentureYear']);
        break;
      case 'DENOVO':
        form.resetFields(['jointVentureYear', 'acquisitionYear', 'preAcquisitionAnnualRevenue', 'vintageName']);
        break;
      case 'JOINTVENTURE':
        form.resetFields(['newOfficeYear', 'acquisitionYear', 'vintageName']);
        break;
    }
    setLocationCategory(LocationCategory[value]);
  };

  const content = (key: any, value: any, data: any) => {
    const params = {
      key,
      label: value.label,
      name: key,
      rules: value.rules,
      ...(value.type === 'checkbox' && { valuePropName: 'checked' }),
      disabled: value.disabled,
    };
    const formItemParams = {
      ...value,
      data: data(),
      allowDateClear: allowDateClear,
      ...(key === 'mergedInto' && {
        onFocus: onOfficeFocus,
        options: officeList.filter((office) => office.id !== locId).map(({ id, number }) => ({ id, value: number })),
        onSearch: onOfficeSearch,
        onPopupScroll: onOfficesScroll,
        onClear: onOfficeClear,
        data: data.mergedInto,
        isLoading: officeListLoading,
      }),
      ...(key === 'locationCategory' && {
        onSelect: onLocationCategorySelect,
        data: Object.entries(LocationCategory),
      }),
      ...(key === 'closedDate' && {
        onSelect: onClosedDateSelect,
        data: closedDate,
        onConfirmVisible: onConfirmSetVisible,
        isConfirmVisible: isClosedDateConfirmVisible,
        name: value.label,
      }),
      ...(key === 'accountingIntegrationDate' && {
        onSelect: onAccountingIntegrationDateSelect,
        data: accountingIntegrationDate,
        onConfirmVisible: onConfirmSetVisible,
        isConfirmVisible: isAccountingIntegrationDateConfirmVisible,
        name: value.label,
      }),
      ...(key === 'sameOfficeChangeDate' && {
        onSelect: onSameOfficeChangeSelect,
        data: sameOfficeChangeDate,
        onConfirmVisible: onConfirmSetVisible,
        isConfirmVisible: isSameOfficeChangeDateConfirmVisible,
        name: value.label,
      }),
    };
    if (value.isVisible)
      return (
        <Form.Item
          {...params}
          className={value.type === 'checkbox' ? 'checkbox-item' : ''}
          validateTrigger={['onBlur', 'onSubmit']}
        >
          {formItemMap(formItemParams, !isEditAllowed || params.disabled)}
        </Form.Item>
      );
    else if (
      (key === 'newOfficeYear' && locationCategory === 'De Novo') ||
      (key === 'jointVentureYear' && locationCategory === 'Joint Venture') ||
      (key === 'acquisitionYear' && locationCategory === 'Acquisition') ||
      (key === 'preAcquisitionAnnualRevenue' &&
        (locationCategory === 'Acquisition' || locationCategory === 'Joint Venture')) ||
      (key === 'vintageName' && locationCategory === 'Acquisition') ||
      (key === 'legacyName' && locationCategory?.toLowerCase() === LocationCategory.ACQUISITION.toLowerCase())
    ) {
      return (
        <Form.Item
          {...params}
          className={value.type === 'checkbox' ? 'checkbox-item' : ''}
          validateTrigger={['onBlur', 'onSubmit']}
        >
          {formItemMap(formItemParams, !isEditAllowed || params.disabled)}
        </Form.Item>
      );
    } else return null;
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className='mvc-details-form'
      onFinish={onSubmit}
      onValuesChange={() => setIsDisabled(false)}
      initialValues={{
        ...data,
        ...(!!data.closedDate && { closedDate: moment(data.closedDate) }),
        ...(!!data.accountingIntegrationDate && { accountingIntegrationDate: moment(data.accountingIntegrationDate) }),
        ...(!!data.sameOfficeChangeDate && { sameOfficeChangeDate: moment(data.sameOfficeChangeDate) }),
        ...(!!data.mergedInto && {
          mergedInto:
            data.mergedInto.id === undefined
              ? null
              : {
                  id: data.mergedInto.id,
                  value: data.mergedInto.name,
                },
        }),
      }}
      onFieldsChange={(changed: any) => {
        if (changed?.[0]?.name?.some((el) => el === 'sameOfficeSales')) {
          const initialSameOfficeSalesValue = data?.sameOfficeSales;
          const currentSameOfficeSalesValue = changed?.[0]?.value;

          if (isEqual(currentSameOfficeSalesValue, initialSameOfficeSalesValue)) {
            form.setFieldsValue({
              sameOfficeChangeDate: !!data?.sameOfficeChangeDate ? moment(data?.sameOfficeChangeDate) : null,
            });
          } else {
            form.setFieldsValue({ sameOfficeChangeDate: moment() });
          }
        }
      }}
    >
      {Object.entries(selectorMap[title]['config']()).map(([key, value]: any) => {
        const data = () => {
          switch (key) {
            case 'locationCategory':
              return Object.entries(LocationCategory);
          }
        };
        return content(key, value, data);
      })}

      {/* {title !== 'office_address' ? ( */}
      <Form.Item>
        <Button type='primary' htmlType='submit' className={'btn-form-action'} disabled={!isEditAllowed || isDisabled}>
          SAVE
        </Button>
      </Form.Item>
      {/* ) : null} */}
    </Form>
  );
}

export default FinanceDetails;
